import { capitalGlobalProposalsService } from '@/services/proposals/capital-global';
import type { DocusignSignatureResponse } from '@/services/proposals/types';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { capitalGlobalProposalAttachmentsQueryKey } from './use-capital-global-proposal-attachments.hook';
import { capitalGlobalProposalSignatureMethodQueryKey } from './use-capital-global-proposal-signature-method.hook';

export const capitalGlobalDocusignSignatureQueryKey = (policyId: string) =>
  ['capital-global-docusign-signature', policyId] as const;

export const capitalGlobalProposalQueryKey = (policyId: string) =>
  ['proposal', policyId] as const;

const IN_PROGRESS_SIGNATURE_STATUSES = new Set(['sent', 'delivered']);
const SIGNATURE_POLL_INTERVAL_MS = 10_000;

function invalidateProposalListing(
  queryClient: ReturnType<typeof useQueryClient>,
  policyId: string,
) {
  void queryClient.invalidateQueries({ queryKey: ['proposals'] });
  void queryClient.invalidateQueries({
    queryKey: capitalGlobalProposalQueryKey(policyId),
  });
}

function invalidateOnSignatureCompleted(
  queryClient: ReturnType<typeof useQueryClient>,
  policyId: string,
  signature?: DocusignSignatureResponse
) {
  if (signature?.status !== 'completed') return;

  void queryClient.invalidateQueries({
    queryKey: capitalGlobalProposalAttachmentsQueryKey(policyId),
  });
  invalidateProposalListing(queryClient, policyId);
}

export function useCapitalGlobalDocusignSignatureStatus(policyId: string) {
  const trimmed = policyId.trim();

  return useQuery({
    queryKey: capitalGlobalDocusignSignatureQueryKey(trimmed),
    enabled: Boolean(trimmed),
    queryFn: async (): Promise<DocusignSignatureResponse | null> => {
      const response =
        await capitalGlobalProposalsService.getDocusignSignatureStatus(trimmed);

      if (!response.success) {
        throw new Error(
          response.error || 'Erro ao carregar assinatura digital'
        );
      }

      const data = response.data ?? null;
      if (data && IN_PROGRESS_SIGNATURE_STATUSES.has(data.status)) {
        const syncResponse =
          await capitalGlobalProposalsService.syncDocusignSignature(trimmed);

        if (!syncResponse.success || !syncResponse.data) {
          throw new Error(
            syncResponse.error || 'Erro ao sincronizar assinatura digital'
          );
        }

        return syncResponse.data;
      }

      return data;
    },
    refetchInterval: query => {
      const status = query.state.data?.status;
      if (status && IN_PROGRESS_SIGNATURE_STATUSES.has(status)) {
        return SIGNATURE_POLL_INTERVAL_MS;
      }
      return false;
    },
  });
}

export function useCreateCapitalGlobalDocusignSignature(policyId: string) {
  const queryClient = useQueryClient();
  const trimmed = policyId.trim();

  return useMutation({
    mutationFn: async () => {
      const response =
        await capitalGlobalProposalsService.createDocusignSignature(trimmed);

      if (!response.success || !response.data) {
        throw Object.assign(
          new Error(response.error || 'Erro ao iniciar assinatura digital'),
          { statusCode: response.statusCode }
        );
      }

      return response.data;
    },
    onSuccess: data => {
      queryClient.setQueryData(
        capitalGlobalDocusignSignatureQueryKey(trimmed),
        data
      );
      void queryClient.invalidateQueries({
        queryKey: capitalGlobalProposalSignatureMethodQueryKey(trimmed),
      });
      invalidateProposalListing(queryClient, trimmed);
      invalidateOnSignatureCompleted(queryClient, trimmed, data);
    },
  });
}

export function useSyncCapitalGlobalDocusignSignature(policyId: string) {
  const queryClient = useQueryClient();
  const trimmed = policyId.trim();

  return useMutation({
    mutationFn: async () => {
      const response =
        await capitalGlobalProposalsService.syncDocusignSignature(trimmed);

      if (!response.success || !response.data) {
        throw new Error(response.error || 'Erro ao verificar assinatura');
      }

      return response.data;
    },
    onSuccess: data => {
      queryClient.setQueryData(
        capitalGlobalDocusignSignatureQueryKey(trimmed),
        data
      );
      invalidateOnSignatureCompleted(queryClient, trimmed, data);
    },
  });
}

export function useRetryCapitalGlobalDocusignSignature(policyId: string) {
  const queryClient = useQueryClient();
  const trimmed = policyId.trim();

  return useMutation({
    mutationFn: async () => {
      const response =
        await capitalGlobalProposalsService.retryDocusignSignature(trimmed);

      if (!response.success || !response.data) {
        throw new Error(response.error || 'Erro ao retentar assinatura');
      }

      return response.data;
    },
    onSuccess: data => {
      queryClient.setQueryData(
        capitalGlobalDocusignSignatureQueryKey(trimmed),
        data
      );
      invalidateOnSignatureCompleted(queryClient, trimmed, data);
    },
  });
}
